import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => {
    const saved = localStorage.getItem('previu_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Decode the JWT payload to extract the user id (sub claim).
  // The backend does not return id in the login response body,
  // so we read it from the token itself — safe, no secret needed for decode.
  const decodeJwtId = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Spring Boot sets sub = email, but may also set userId/id claim
      // Try id first, fall back to sub (email used as identifier)
      return payload.id || payload.userId || payload.sub || null;
    } catch {
      return null;
    }
  };

  const storeSession = (token, email, firstName, lastName, role, mustChangePassword = false) => {
    const id = decodeJwtId(token);
    // mustChangePassword: the backend sets this when a password was chosen FOR
    // this user (an admin reset, or generated at doctor creation) and not yet
    // rotated. Persisted with the session so a reload mid-flow doesn't drop the
    // requirement. Always false for SSO accounts — they have no password.
    const userData = {
      id, email, name: `${firstName} ${lastName}`, firstName, lastName, role,
      mustChangePassword: !!mustChangePassword,
    };
    localStorage.setItem('previu_token', token);
    localStorage.setItem('previu_user', JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.login(email, password);
      const { token, email: userEmail, firstName, lastName, role, mustChangePassword } = res.data;
      storeSession(token, userEmail, firstName, lastName, role, mustChangePassword);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (idToken) => {
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.googleLogin(idToken);
      const { token, email, firstName, lastName, role, mustChangePassword } = res.data;
      storeSession(token, email, firstName, lastName, role, mustChangePassword);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed. Ensure you use a @previu.health account.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = async (phone) => {
    setLoading(true);
    setError('');
    try {
      await authAPI.requestOTP(phone);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Check your number and try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (phone, otp) => {
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.verifyOTP(phone, otp);
      const { token, email, firstName, lastName, role, mustChangePassword } = res.data;
      storeSession(token, email, firstName, lastName, role, mustChangePassword);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Called after a successful self-service password change so the forced-change
  // gate stops firing without needing a re-login.
  const clearMustChangePassword = () => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, mustChangePassword: false };
      localStorage.setItem('previu_user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem('previu_token');
    localStorage.removeItem('previu_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, googleLogin, requestOTP, verifyOTP, logout, clearMustChangePassword, loading, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
